import type { TBlockConfig } from '@conversed/shared/src/central/types/block.type';
import type {
  Decider,
  DeciderLogic,
  TDecideRule,
} from '@conversed/shared/src/central/types/blocks/Decider';
import { range } from '@conversed/shared/src/helpers/data.helpers';
import type { TBlockConfigEdge } from '@interfaces/TReactflow';
import { MarkerType, type Node, Position } from '@xyflow/react';
import { Types } from 'mongoose';
import { generate } from 'short-uuid';
import { ROUTER_CONNECTION_LIMITS } from '../constants/flow';
import { EFlowDirection } from '../interfaces/EFlowDirection';
import type { TGetLoopBackStepEdge, TNodeType } from '../interfaces/TDesigner';

export const getBlockTypeByNode = (nodeType: TNodeType) => {
  switch (nodeType) {
    case 'textNode':
      return 'text';
    case 'llmNode':
      return 'llm';
    case 'reminderNode':
      return 'reminder';
    case 'textV2Node':
      return 'text-v2';
    case 'buttonNode':
      return 'button';
    case 'shortcutNode':
      return 'shortcut';
    case 'dataNode':
      return 'databaseUpdate';
    case 'imageNode':
      return 'StandAloneImage';
    case 'feedbackNode':
      return 'feedback';
    case 'routerNode':
      return 'Decider';
    case 'flowNode':
      return 'text';
    case 'gateNode':
      return 'gate';
    case 'handoverNode':
      return 'handover';
    case 'carouselNode':
      return 'imageCarousel';
    case 'sliderNode':
      return 'sliderNode';
    case 'otpNode':
      return 'otp';
    case 'endNode':
      return 'end';
    case 'intentCheckNode':
      return 'intent-check';
  }
};
export const getHandlePositionByFlowDirection = (
  flowDirection: EFlowDirection,
) => {
  if (flowDirection === EFlowDirection.Vertical) {
    return {
      start: Position.Top,
      end: Position.Bottom,
      middle: Position.Right,
    };
  }

  return {
    start: Position.Left,
    end: Position.Right,
    middle: Position.Bottom,
  };
};

const getNodeDimensions = (nodes: Node[]) =>
  nodes.map((node) => {
    if (!node) {
      return {
        height: 1,
        width: 1,
      };
    }

    return {
      height: node.measured?.height ?? 1,
      width: node.measured?.width ?? 1,
    };
  });

export const getLoopbackStepEdge = (
  options: TGetLoopBackStepEdge,
): { d: string; labelX: number; labelY: number } => {
  const { source, target, sourceXYP, targetXYP } = options;
  const { x: sourceX, y: sourceY } = sourceXYP;
  const { x: targetX, y: targetY } = targetXYP;

  const padding = 24;

  const [{ width: sourceWidth, height: sourceHeight }, { width: targetWidth }] =
    getNodeDimensions([source, target]);

  // * Use the node's top-left position to compute absolute edges.
  // * sourceX/Y and targetX/Y are handle coordinates, which may be at the node's
  // * side or middle (e.g. a left-side handle at top:33%), so we cannot derive
  // * node boundaries from them reliably.
  const sourceNodeLeft = source.position.x;
  const sourceNodeRight = source.position.x + sourceWidth;
  const sourceNodeBottom = source.position.y + sourceHeight;
  const targetNodeLeft = target.position.x;
  const targetNodeRight = target.position.x + targetWidth;
  const targetNodeTop = target.position.y;
  const sourceCenterX = sourceNodeLeft + sourceWidth / 2;
  const targetCenterX = targetNodeLeft + targetWidth / 2;

  // * Route around the side whose nodes are furthest out, avoiding both nodes entirely.
  const loopAroundLeft = sourceCenterX > targetCenterX;
  const sideX = loopAroundLeft
    ? Math.min(sourceNodeLeft, targetNodeLeft) - padding
    : Math.max(sourceNodeRight, targetNodeRight) + padding;

  // * Use node boundaries (not handle coordinates) so that a mid-node handle
  // * (e.g. a side handle at 33% height) does not cause the path to clip through
  // * the node body vertically.
  const belowSourceY = sourceNodeBottom + padding;
  const aboveTargetY = targetNodeTop - padding;

  // * Route: source handle → down → side → up → target handle
  const startPoint = `${sourceX},${sourceY}`;
  const p1 = `${sourceX},${belowSourceY}`;
  const p2 = `${sideX},${belowSourceY}`;
  const p3 = `${sideX},${aboveTargetY}`;
  const p4 = `${targetX},${aboveTargetY}`;
  const endPoint = `${targetX},${targetY}`;

  return {
    d: `M ${startPoint} L ${p1} L ${p2} L ${p3} L ${p4} L ${endPoint}`,
    labelX: sideX,
    labelY: (belowSourceY + aboveTargetY) / 2,
  };
};

const edgeStyles = {
  entry: {
    style: { stroke: '#000' },
    arrowHeadType: { type: MarkerType.ArrowClosed, color: '#f00' },
  },
  destination: {
    style: { stroke: '#000' },
    arrowHeadType: { type: MarkerType.ArrowClosed, color: '#f00' },
  },
  contains: {
    style: { stroke: '#f6ab6c' },
    arrowHeadType: { type: MarkerType.ArrowClosed, color: '#f00' },
  },
  decider: {
    style: { stroke: '#000' },
    arrowHeadType: { type: MarkerType.ArrowClosed, color: '#f00' },
  },
  validator: {
    style: { stroke: '#33daca' },
    arrowHeadType: { type: MarkerType.ArrowClosed, color: '#f00' },
  },
  error: {
    style: { stroke: '#EB5757' },
    arrowHeadType: { type: MarkerType.ArrowClosed, color: '#f00' },
  },
} as const;

export const parseEdge = (
  id: string,
  source: string,
  destination: string,
  style: keyof typeof edgeStyles,
  handle?: string,
  label?: string,
): TBlockConfigEdge => {
  const { arrowHeadType, style: edgeStyle } = edgeStyles[style];

  return {
    zIndex: 9,
    id: id,
    type: 'loopbackStep' as never,
    source,
    sourceHandle: handle ?? 'end',
    style: edgeStyle,
    markerStart: arrowHeadType,
    markerEnd: arrowHeadType,
    target: destination,
    ...(label !== undefined && { label }),
  };
};

export const getConnectionLimitByRouterParams = (params: Decider['params']) => {
  const { logic, modulo } = params;

  switch (logic) {
    case 'decideByOrderOfUser': {
      return modulo ?? 1;
    }
    case 'decideIfCouponsForTypeExist': {
      return ROUTER_CONNECTION_LIMITS.decideIfCouponsForTypeExist;
    }
    case 'decideByWorkingHours': {
      return ROUTER_CONNECTION_LIMITS.decideByWorkingHours;
    }
    case 'decideByRecentLLMQuestionThreshold': {
      return ROUTER_CONNECTION_LIMITS.decideByRecentLLMQuestionThreshold;
    }
    default: {
      return ROUTER_CONNECTION_LIMITS.default;
    }
  }
};

type TDeciderDestination = Decider['params']['possibleDestinations'][0];

// * Should return a value T, or undefined
export const findConditionNotPresentInDecider = <T>(
  condition: T[],
  possibleDestinations: Array<TDeciderDestination>,
) => {
  const checkRulesForDestination = (destination: TDeciderDestination) =>
    destination?.rules?.flatMap(checkOperandInRule);
  const checkOperandInRule = (rule: TDecideRule) =>
    'operand' in rule ? rule.operand : undefined;

  return condition.find((value) => {
    const destinations = possibleDestinations ?? [];
    const presentOperands = destinations
      ?.flatMap(checkRulesForDestination)
      .filter(Boolean);

    // * Check to see if the value of our condition is part of the operands
    return !presentOperands.includes(value);
  });
};

export const getRulesByRouterParams = (params: Decider['params']) => {
  const { logic, modulo, possibleDestinations } = params;

  switch (logic) {
    case 'decideByOrderOfUser': {
      // * Find first number that is not in an operand from 0 to modulo
      const numbersInModulo = range(0, modulo ?? 2 - 1);
      const unusedNumberInModulo = findConditionNotPresentInDecider(
        numbersInModulo,
        possibleDestinations,
      );

      if (typeof unusedNumberInModulo === 'undefined') {
        throw new Error('Not valid for destination');
      }

      return [{ operator: 'eq' as const, operand: unusedNumberInModulo }];
    }
    case 'decideIfCouponsForTypeExist': {
      const conditionsAvailable = [true, false];
      const unusedCondition = findConditionNotPresentInDecider(
        conditionsAvailable,
        possibleDestinations,
      );

      if (typeof unusedCondition === 'undefined') {
        throw new Error('Not valid for destination');
      }

      return [{ operator: 'eq' as const, operand: unusedCondition }];
    }
    case 'decideByWorkingHours': {
      // * Same binary true/false branching as decideIfCouponsForTypeExist
      const conditionsAvailable = [true, false];
      const unusedCondition = findConditionNotPresentInDecider(
        conditionsAvailable,
        possibleDestinations,
      );

      if (typeof unusedCondition === 'undefined') {
        throw new Error('Not valid for destination');
      }

      return [{ operator: 'eq' as const, operand: unusedCondition }];
    }
    case 'decideByRecentLLMQuestionThreshold': {
      // * Two possible outcomes: 1 (threshold reached) or 0 (below threshold).
      // * Auto-assign whichever operand has not yet been claimed by an existing destination.
      const conditionsAvailable = [1, 0];
      const unusedCondition = findConditionNotPresentInDecider(
        conditionsAvailable,
        possibleDestinations,
      );

      if (typeof unusedCondition === 'undefined') {
        throw new Error('Not valid for destination');
      }

      return [{ operator: 'eq' as const, operand: unusedCondition }];
    }
  }
};

// * Human-readable symbols for each rule operator, used in decider edge labels.
export const OPERATOR_SYMBOLS: Record<string, string> = {
  eq: '=',
  ne: '≠',
  gt: '>',
  lt: '<',
  ge: '≥',
  le: '≤',
  exists: '∃',
  includes: '⊂',
  startsWith: '^',
  endsWith: '$',
};

// * Decider logic types where destinations are bounded and rules are auto-generated.
// * These get per-destination handles on the RouterNode instead of all sharing 'end'.
export const FIXED_SET_DECIDER_TYPES: DeciderLogic[] = [
  'decideIfCouponsForTypeExist',
  'decideByOrderOfUser',
  'decideByWorkingHours',
  'decideByRecentLLMQuestionThreshold',
];

// Recursively updates references to a specific ID within an object or array.
// This function traverses through the provided object or array and replaces any occurrences of the old ID with the new ID.
// It handles nested structures by recursively calling itself for nested objects and arrays.
const updateReferencesInObject = (obj: any, oldId: string, newId: string) => {
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      if (item === oldId) {
        obj[index] = newId;
      } else if (typeof item === 'object' && item !== null) {
        updateReferencesInObject(item, oldId, newId);
      }
    });
  } else if (typeof obj === 'object' && obj !== null) {
    Object.keys(obj).forEach((key: string) => {
      if (obj[key] === oldId) {
        obj[key] = newId;
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        updateReferencesInObject(obj[key], oldId, newId);
      }
    });
  }
};

export const updateReferencesInBlocks = (
  blocks: TBlockConfig[],
  oldId: string,
  newId: string,
) => {
  blocks.forEach((block: TBlockConfig) => {
    updateReferencesInObject(block, oldId, newId);
  });
};

export const getBlocksWithNewIdAndSubFlow = (
  blocks: TBlockConfig[],
  subflowName: string,
  revisionId?: string,
): TBlockConfig[] => {
  const blocksCopy = structuredClone(blocks);

  // Remove blocks with type "version" and update the remaining blocks
  // "version" block is not needed in the subflow template, because it is a default one of the main flow
  return blocksCopy
    .filter((block) => block.type !== 'version')
    .map((block: TBlockConfig) => {
      const newId = generate();
      if ('_id' in block) {
        block._id = new Types.ObjectId();
      }

      if ('_v' in block) {
        block._v = 0;
      }

      // Remove the routeToSubFlow field from gate blocks to prevent routing to a wrong subflow.
      // If we remove the routeToSubFlow field value from the gate block, a warning will be displayed on the gate block on the designer page, indicating that the block is not routed to any subflow.
      if (block.type === 'gate' && block.routeToSubFlow) {
        block.routeToSubFlow = '';
      }

      // Update references in all blocks, otherwise the blocks will not be connected to each other.
      updateReferencesInBlocks(blocksCopy, block.id, newId);

      return {
        ...block,
        id: newId, // Overwrite the existing block ID with a new one to prevent duplication after adding the subflow template.
        subFlow: subflowName, // Update the subFlow field to the new subflow name to ensure the block belongs to the new template.
        revisionId,
      };
    });
};
